import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext.jsx";
import { useNotifications } from "../../context/NotificationContext.jsx";
import { useMobileMenu } from "../../context/MobileMenuContext.jsx";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { unreadCount }  = useNotifications();

  const [loggingOut, setLoggingOut] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // ── Menu mobile ──────────────────────────────────────────────────────
  // Corrigé le 15/09/2026 : en dessous de 768px la sidebar passait en
  // `display:none` sans rien pour la remplacer → le menu disparaissait
  // purement et simplement en mode téléphone, sans aucun moyen de naviguer.
  // Elle devient maintenant un tiroir qui glisse depuis la gauche. L'état
  // d'ouverture vient de MobileMenuContext (partagé avec le bouton ☰ affiché
  // dans Topbar) plutôt que d'un état local, pour éviter un second bouton
  // flottant qui se superposait au titre de chaque page.
  const { isOpen: mobileOpen, close: closeMobileMenu } = useMobileMenu();

  const handleLogoutClick = () => { closeMobileMenu(); setShowLogoutConfirm(true); };

  const handleLogoutConfirm = () => {
    setLoggingOut(true);
    logout()
      .then(() => navigate("/login"))
      .catch(() => navigate("/login"));
  };

  const handleLogoutCancel = () => setShowLogoutConfirm(false);

  // ── Navigation admin — correspond aux routes définies dans App.jsx ──
  const NAV = [
    {
      section: "Principal",
      items: [
        { to: "/dashboard",     icon: "bi-grid-1x2-fill",          label: "Dashboard" },
        { to: "/tickets",       icon: "bi-ticket-detailed-fill",    label: "Tickets" },
        { to: "/assets",        icon: "bi-pc-display-horizontal",   label: "Parc informatique" },
        { to: "/inventory",     icon: "bi-clipboard2-check-fill",   label: "Inventaire" },
        { to: "/passwords",     icon: "bi-shield-lock-fill",        label: "Mots de passe" },
        { to: "/my-passwords",  icon: "bi-person-lock",             label: "Mes mots de passe" },
        { to: "/vault-export", icon: "bi-shield-lock",         label: "Coffre de secours" },
        { to: "/notifications", icon: "bi-bell-fill",               label: "Notifications", badge: unreadCount },
      ],
    },
    {
      section: "Gestion",
      items: [
        { to: "/clients",    icon: "bi-building",             label: "Entreprises" },
        { to: "/users",      icon: "bi-people-fill",           label: "Utilisateurs" },
        { to: "/billing",    icon: "bi-cash-coin",             label: "Contrats" },
       // { to: "/reports", icon: "bi-file-earmark-bar-graph-fill", label: "Rapports" },
        { to: "/access-log", icon: "bi-journal-text",          label: "Journal d'accès" },
        { to: "/sync",       icon: "bi-arrow-repeat",          label: "Sync Atera" },
        { to: "/settings",   icon: "bi-gear-fill",             label: "Paramètres" },
      ],
    },
  ];

  return (
    <>
      {/* ── Arrière-plan assombri derrière le tiroir ouvert ── */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={closeMobileMenu} />
      )}

      <aside className={`itm-sidebar ${mobileOpen ? "mobile-open" : ""}`}>

        {/* ── Logo CPUc ── */}
        <div className="sidebar-logo">
          <img src="/logo-cpuc.png" alt="CPUc" className="sidebar-logo-img"/>
        </div>

        {/* ── Navigation ── */}
        <nav className="sidebar-nav">
          {NAV.map((group) => (
            <div key={group.section}>
              <div className="nav-section-label">{group.section}</div>
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={closeMobileMenu}
                  className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
                  <i className={`bi ${item.icon}`}/>
                  <span>{item.label}</span>
                  {item.badge > 0 && <span className="sidebar-badge">{item.badge}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* ── Footer ── */}
        <div className="sidebar-footer">
          <div className="user-pill" onClick={() => { closeMobileMenu(); navigate("/profile"); }}>
            <div className="avatar-pill">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
            <div className="user-info">
              <div className="user-name">{user?.firstName} {user?.lastName}</div>
              <div className="user-role">{user?.role?.name ?? "Admin"}</div>
            </div>
          </div>

          <button
            className="logout-btn"
            onClick={handleLogoutClick}
            disabled={loggingOut}
            title="Se déconnecter"
          >
            {loggingOut
              ? <><i className="bi bi-arrow-repeat logout-spin"/><span>Déconnexion…</span></>
              : <><i className="bi bi-box-arrow-right"/><span>Déconnexion</span></>
            }
          </button>
        </div>

        {/* ── Modale de confirmation — centrée à l'écran ── */}
        {showLogoutConfirm && (
          <div className="logout-confirm-overlay" onClick={e => e.target === e.currentTarget && handleLogoutCancel()}>
            <div className="logout-confirm-box">
              <div className="logout-confirm-icon"><i className="bi bi-box-arrow-right"/></div>
              <div className="logout-confirm-title">Se déconnecter ?</div>
              <p className="logout-confirm-sub">Tu devras te reconnecter pour accéder à ton compte.</p>
              <div className="logout-confirm-actions">
                <button className="logout-confirm-cancel" onClick={handleLogoutCancel} disabled={loggingOut}>Annuler</button>
                <button className="logout-confirm-yes" onClick={handleLogoutConfirm} disabled={loggingOut}>
                  {loggingOut ? <><i className="bi bi-arrow-repeat logout-spin me-1"/>Déconnexion…</> : "Se déconnecter"}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{SIDEBAR_CSS}</style>
      </aside>
    </>
  );
}

const SIDEBAR_CSS = `
/* ── Sidebar container ── */
.itm-sidebar {
  width: var(--sidebar-w);
  background: #fff;
  border-right: 1px solid rgba(0,0,0,.08);
  display: flex; flex-direction: column;
  position: fixed; top: 0; left: 0; bottom: 0;
  z-index: 100; overflow: hidden;
  transition: width .3s cubic-bezier(.4,0,.2,1);
  box-shadow: 2px 0 12px rgba(0,0,0,.06);
}

/* ── Logo ── */
.sidebar-logo {
  padding: 20px 18px;
  display: flex; align-items: center; gap: 10px;
  border-bottom: 1px solid rgba(0,0,0,.07); flex-shrink: 0;
}
.sidebar-logo-img {
  height: 32px; object-fit: contain; flex-shrink: 0;
}

/* ── Nav ── */
.sidebar-nav { flex: 1; padding: 10px 0; overflow-y: auto; overflow-x: hidden; }
.nav-section-label {
  font-size: 10px; font-weight: 600; letter-spacing: .12em; color: #9CA3AF;
  padding: 10px 18px 4px; text-transform: uppercase; white-space: nowrap;
}
.sidebar-item {
  display: flex; align-items: center; gap: 11px;
  padding: 9px 18px; cursor: pointer;
  border-left: 3px solid transparent;
  transition: all .18s; color: #4B5563;
  font-size: 13.5px; white-space: nowrap; text-decoration: none;
}
.sidebar-item i { font-size: 17px; flex-shrink: 0; }
.sidebar-item:hover { color: #1A1A1A; background: rgba(0,0,0,.04); }
.sidebar-item.active {
  color: #E63329;
  border-left-color: #E63329;
  background: rgba(230,51,41,.07);
  font-weight: 500;
}
.sidebar-item.active i { color: #E63329; }
.sidebar-badge {
  margin-left: auto; background: #E63329; color: #fff;
  border-radius: 20px; font-size: 10px; font-weight: 700;
  padding: 2px 7px; flex-shrink: 0;
}

/* ── Footer ── */
.sidebar-footer { padding: 12px; border-top: 1px solid rgba(0,0,0,.07); flex-shrink: 0; }
.user-pill {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 10px; cursor: pointer;
  transition: background .18s; overflow: hidden; margin-bottom: 6px;
}
.user-pill:hover { background: rgba(0,0,0,.04); }
.avatar-pill {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #E63329, #c92b22);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #fff; text-transform: uppercase;
}
.user-info { overflow: hidden; min-width: 0; }
.user-name { font-size: 13px; font-weight: 500; color: #1A1A1A; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.user-role { font-size: 11px; color: #9CA3AF; }

/* ── Logout ── */
.logout-btn {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 10px; width: 100%;
  background: none; border: none; cursor: pointer;
  color: #9CA3AF; font-size: 13px; transition: all .2s;
}
.logout-btn:not(:disabled):hover {
  color: #DC2626; background: rgba(220,38,38,.08);
}
.logout-btn:disabled { cursor: not-allowed; opacity: .6; }
.logout-btn i { font-size: 16px; flex-shrink: 0; }

/* ── Modale de confirmation de déconnexion — centrée à l'écran ── */
.logout-confirm-overlay {
  position: fixed; inset: 0; z-index: 2000;
  background: rgba(26,26,26,.55); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: 16px; animation: logoutFade .15s ease;
}
@keyframes logoutFade { from { opacity: 0; } to { opacity: 1; } }
.logout-confirm-box {
  background: #fff; border-radius: 16px; padding: 28px 26px 22px;
  width: 100%; max-width: 340px; text-align: center;
  box-shadow: 0 32px 100px rgba(0,0,0,.3);
  animation: logoutSlide .2s cubic-bezier(.34,1.56,.64,1);
}
@keyframes logoutSlide { from { transform: translateY(20px); opacity: 0; } to { transform: none; opacity: 1; } }
.logout-confirm-icon {
  width: 48px; height: 48px; border-radius: 50%; margin: 0 auto 14px;
  background: rgba(220,38,38,.1); color: #DC2626;
  display: flex; align-items: center; justify-content: center; font-size: 20px;
}
.logout-confirm-title { font-size: 16px; font-weight: 700; color: #1A1A1A; margin-bottom: 6px; }
.logout-confirm-sub { font-size: 13px; color: #9CA3AF; margin: 0 0 20px; }
.logout-confirm-actions { display: flex; gap: 10px; }
.logout-confirm-cancel, .logout-confirm-yes {
  flex: 1; padding: 9px; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: all .15s; border: none;
}
.logout-confirm-cancel { background: #F4F5F7; color: #4B5563; }
.logout-confirm-cancel:hover { background: rgba(0,0,0,.1); color: #1A1A1A; }
.logout-confirm-yes { background: #DC2626; color: #fff; display: flex; align-items: center; justify-content: center; }
.logout-confirm-yes:hover { filter: brightness(1.1); }
.logout-confirm-cancel:disabled, .logout-confirm-yes:disabled { opacity: .6; cursor: not-allowed; }
@keyframes logout-spin { to { transform: rotate(360deg); } }
.logout-spin { animation: logout-spin .7s linear infinite; display: inline-block; }

/* ── Arrière-plan derrière le tiroir mobile ── */
.sidebar-overlay {
  display: none;
  position: fixed; inset: 0; z-index: 149;
  background: rgba(26,26,26,.5);
  animation: logoutFade .15s ease;
}

/* ── Responsive ── */
@media (max-width: 1100px) {
  .nav-section-label, .sidebar-item span,
  .sidebar-badge, .user-info, .logout-btn span,
  .logout-cancel { display: none !important; }
  .sidebar-item { justify-content: center; padding: 10px 0; border-left-width: 0; }
  .sidebar-item.active { background: rgba(230,51,41,.07); }
  .sidebar-logo { justify-content: center; padding: 18px 10px; }
  .sidebar-logo-img { height: 28px; }
  .user-pill { justify-content: center; }
  .logout-btn { justify-content: center; }
}

@media (max-width: 768px) {
  .sidebar-overlay { display: block; }

  /* Le rail replié (icônes seules) ne sert à rien sur téléphone : la
     sidebar devient un tiroir plein qui glisse depuis la gauche, caché
     par défaut et ouvert via le bouton flottant ci-dessus. */
  .itm-sidebar {
    width: 260px;
    transform: translateX(-100%);
    transition: transform .28s cubic-bezier(.4,0,.2,1);
    z-index: 150;
  }
  .itm-sidebar.mobile-open { transform: translateX(0); }

  .itm-sidebar .nav-section-label,
  .itm-sidebar .sidebar-item span,
  .itm-sidebar .user-info,
  .itm-sidebar .logout-btn span { display: block !important; }
  .itm-sidebar .sidebar-badge { display: inline-block !important; }
  .itm-sidebar .sidebar-item { justify-content: flex-start; padding: 9px 18px; border-left-width: 3px; }
  .itm-sidebar .sidebar-logo { justify-content: flex-start; padding: 20px 18px 20px 64px; }
  .itm-sidebar .sidebar-logo-img { height: 32px; }
  .itm-sidebar .user-pill { justify-content: flex-start; }
  .itm-sidebar .logout-btn { justify-content: flex-start; }
}
`;